import { useCallback, useMemo } from "react";

import { CustomerBalanceRule } from "../../services";
import { useTypedSelector } from "../../redux/store";
import { sortByNumberOrString } from "../../utils";

const useCustomerBalanceRuleSubHandler =
	(): useCustomerBalanceRuleSubHandler.Props => {
		const customerBalanceRuleSubscribe = useTypedSelector(
			(state) =>
				state.globalState.customerBalanceRule.data.subscription.basic,
		);

		const getAllCustomerBalanceRuleSubHandler = useCallback(
			({
				limit,
				taxiServiceIds,
				order,
			}: useCustomerBalanceRuleSubHandler.SmallSubscribeOptions): CustomerBalanceRule.Model[] => {
				if (!Array.isArray(customerBalanceRuleSubscribe)) {
					return [];
				}

				let retval = [...customerBalanceRuleSubscribe];

				if (
					Array.isArray(taxiServiceIds) &&
					taxiServiceIds.length > 0
				) {
					retval = retval.filter(
						(item) =>
							Array.isArray(item.taxiServiceIds) &&
							item.taxiServiceIds.some((id) =>
								taxiServiceIds.includes(id),
							),
					);
				}

				if (order && typeof order === "object") {
					const [[dataKey, sortType] = []] = Object.entries(order);
					if (
						typeof dataKey === "string" &&
						typeof sortType === "string" &&
						["asc", "desc"].includes(sortType.toLowerCase())
					) {
						const sortOrder =
							sortType.toLowerCase() === "asc" ? 1 : -1;
						retval = retval.sort((left, right) => {
							const leftValue = left[dataKey as keyof typeof left] as
								| string
								| number
								| undefined;
							const rightValue = right[
								dataKey as keyof typeof right
							] as string | number | undefined;
							return (
								sortByNumberOrString(
									leftValue ?? "",
									rightValue ?? "",
									true,
								) * sortOrder
							);
						});
					}
				}

				if (typeof limit === "number") {
					retval = retval.slice(0, limit);
				}

				return retval;
			},
			[customerBalanceRuleSubscribe],
		);

		return useMemo(
			() => ({
				getAllCustomerBalanceRuleSubHandler,
			}),
			[getAllCustomerBalanceRuleSubHandler],
		);
	};

declare namespace useCustomerBalanceRuleSubHandler {
	interface Props {
		getAllCustomerBalanceRuleSubHandler: (
			data: SmallSubscribeOptions,
		) => CustomerBalanceRule.Model[];
	}

	interface SmallSubscribeOptions {
		taxiServiceIds?: number[];
		limit?: number;
		order?: CustomerBalanceRule.SubscribeOptions["order"];
	}
}

export default useCustomerBalanceRuleSubHandler;
